// src/pages/ChatWithAI/components/ToolResult.js
import React from 'react';
import './ToolResult.css';

/**
 * @typedef {import('../types/toolTypes').ToolExecutionResult} ToolExecutionResult
 */

/**
 * Componente para exibir resultados de tools.
 * Renderiza diferentes visualizações dependendo do tipo de tool.
 * 
 * @param {Object} props
 * @param {string} props.toolName - Nome da tool
 * @param {ToolExecutionResult} props.result - Resultado da execução
 * @param {Function} [props.onAction] - Callback para ações (download, anexar, etc)
 */
const ToolResult = ({ toolName, result, onAction }) => {
  if (!result || !result.success) {
    return null;
  }

  // Renderizar resultado específico por tool
  switch (toolName) {
    case 'search_jurisprudencia':
      return <SearchJurisprudenciaResult result={result} onAction={onAction} />;
    case 'gerar_documento':
      return <GerarDocumentoResult result={result} onAction={onAction} />;
    case 'atualizar_caso':
      return <AtualizarCasoResult result={result} onAction={onAction} />;
    default:
      return <GenericToolResult result={result} onAction={onAction} />;
  }
};

/**
 * Resultado de busca de jurisprudência
 */
const SearchJurisprudenciaResult = ({ result, onAction }) => {
  const data = result.data || [];
  const metadata = result.metadata || {};

  const handleOpenAllLinks = () => {
    data.forEach((acordao) => {
      if (acordao.url_original) {
        window.open(acordao.url_original, '_blank');
      }
    });
  };

  const handleAttachToCase = () => {
    if (onAction) {
      onAction('attach_to_case', { acordaos: data });
    }
  };

  return (
    <div className="tool-result search-jurisprudencia">
      <div className="result-header">
        <h4>📚 Resultados da Pesquisa</h4>
        <span className="result-count">{data.length} acórdão(s) encontrado(s)</span>
      </div>

      {metadata.query && (
        <div className="result-query">
          <strong>Pesquisa:</strong> {metadata.query}
        </div>
      )}

      {metadata.tribunais && metadata.tribunais.length > 0 && (
        <div className="result-tribunais">
          <strong>Tribunais:</strong> {metadata.tribunais.join(', ')}
        </div>
      )}

      <div className="acordaos-list">
        {data.map((acordao, index) => (
          <div key={acordao.id || index} className="acordao-card">
            <div className="acordao-header">
              <span className="acordao-tribunal">{acordao.tribunal_origem}</span>
              <span className="acordao-processo">{acordao.processo_numero}</span>
            </div>
            
            <div className="acordao-meta">
              <span className="acordao-data">
                📅 {new Date(acordao.data_acordao).toLocaleDateString('pt-PT')}
              </span>
              {acordao.relator && (
                <span className="acordao-relator">👤 {acordao.relator}</span>
              )}
            </div>

            {acordao.sumario && (
              <p className="acordao-sumario">{acordao.sumario}</p>
            )}

            {acordao.tags && acordao.tags.length > 0 && (
              <div className="acordao-tags">
                {acordao.tags.slice(0, 3).map((tag, idx) => (
                  <span key={idx} className="acordao-tag">{tag}</span>
                ))}
              </div>
            )}

            {acordao.url_original && (
              <a
                href={acordao.url_original}
                target="_blank"
                rel="noopener noreferrer"
                className="acordao-link"
              >
                🔗 Ver Acórdão Completo
              </a>
            )}
          </div>
        ))}
      </div>

      {data.length > 0 && (
        <div className="result-actions">
          <button
            className="result-action-btn secondary"
            onClick={handleOpenAllLinks}
          >
            🔗 Abrir Todos os Links
          </button>
          <button
            className="result-action-btn primary"
            onClick={handleAttachToCase}
          >
            📎 Anexar ao Caso
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Resultado de geração de documento
 */
const GerarDocumentoResult = ({ result, onAction }) => {
  const data = result.data || {};

  const handleDownload = () => {
    if (onAction) {
      onAction('download', { filename: data.filename });
    }
  };

  const handleAttach = () => {
    if (onAction) {
      onAction('attach_to_case', { filename: data.filename });
    }
  };

  return (
    <div className="tool-result gerar-documento">
      <div className="result-header">
        <h4>📄 Documento Gerado</h4>
      </div>

      <div className="document-info">
        <div className="info-row">
          <strong>Tipo:</strong>
          <span>{data.tipoDocumento}</span>
        </div>
        <div className="info-row">
          <strong>Formato:</strong>
          <span>{data.formato?.toUpperCase() || 'DOCX'}</span>
        </div>
        <div className="info-row">
          <strong>Arquivo:</strong>
          <span className="filename">{data.filename}</span>
        </div>
      </div>

      {data.message && (
        <div className="stub-notice">
          ℹ️ {data.message}
        </div>
      )}

      <div className="result-actions">
        <button
          className="result-action-btn primary"
          onClick={handleDownload}
        >
          📥 Download Documento
        </button>
        <button
          className="result-action-btn secondary"
          onClick={handleAttach}
        >
          📎 Anexar ao Caso
        </button>
      </div>
    </div>
  );
};

/**
 * Resultado de atualização de caso
 */
const AtualizarCasoResult = ({ result, onAction }) => {
  const data = result.data || {};

  return (
    <div className="tool-result atualizar-caso">
      <div className="result-header">
        <h4>✏️ Caso Atualizado</h4>
      </div>

      <div className="update-info">
        <div className="info-row">
          <strong>ID do Caso:</strong>
          <span>{data.casoId}</span>
        </div>
        <div className="info-row">
          <strong>Campos Atualizados:</strong>
          <span>{data.updatedFields?.join(', ') || 'Nenhum'}</span>
        </div>
      </div>

      {data.message && (
        <div className="stub-notice">
          ℹ️ {data.message}
        </div>
      )}

      <div className="result-actions">
        <button
          className="result-action-btn primary"
          onClick={() => onAction && onAction('view_caso', { casoId: data.casoId })}
        >
          👁️ Ver Caso Atualizado
        </button>
      </div>
    </div>
  );
};

/**
 * Resultado genérico para tools desconhecidas
 */
const GenericToolResult = ({ result }) => {
  return (
    <div className="tool-result generic">
      <div className="result-header">
        <h4>✅ Resultado</h4>
      </div>
      <pre className="result-data">
        {JSON.stringify(result.data, null, 2)}
      </pre>
    </div>
  );
};

export default ToolResult;